import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { Leg, OrGroups } from "./types/flowchart";
import type { StrategyTicketContext } from "./types/context";
import { ConfiguredSignal, generateId, SIGNAL_REGISTRY } from "../../../config/signals";
import { LegCard } from "./flowchart/LegCard";
import { SymbolBank } from "../../../components/common/SymbolBank";
import { ItemWrap } from "./flowchart/ItemWrap";
import { OrGroupEditor } from "./flowchart/OrGroupEditor";
import { TradeCondition } from "./flowchart/TradeConditions";
import type { CopiedLegPayload, CopiedSignalPayload } from "./flowchart/signals/types";
import {
  generateBacktestRequest,
  validateBacktestConfig,
  parseBacktestConfig,
  UIBacktestState,
  BacktestRequest,
  type ValidationError,
} from "./config";
import { BacktestProgress } from "./services/Backtest";
import {
  AssistantMessagePayload,
  BacktestStreamingError,
  backtestService
} from "./services/BacktestService";
import { tradingService } from "../../../services/TradingService";
import { useAuth } from "../../../contexts/AuthContext";
import { useOptionalOrderTicketNavigation } from "../navigation/OrderTicketNavigationContext";
import { useGlobalSession } from "../../../contexts/GlobalSessionContext";
import { useJobs } from "../../../contexts/JobContext";
import { DatePicker } from "../../../components/common/DatePicker";
import { TickerDropdown } from "../../../components/common/TickerDropdown";
import { InfoTooltip } from "../../../components/common/InfoTooltip";
import { FALLBACK_STOCK_UNIVERSE_DATA, loadStockUniverse } from "../../../services/StockUniverseService";
import { StrategyConfigView } from "./components/StrategyConfigView";
import {
  buildStrategyOrderStorageKey,
  detectSessionIdFromPath,
  readSessionStorageJson,
  removeSessionStorageKey,
  writeSessionStorageJson,
} from "../persistence/orderTicketPersistence";
import styles from "./BacktestConfig.module.css";
import logger from '../../../logging/logger';

const newId = () => generateId();
const cloneSignalParams = (params: Record<string, unknown>): Record<string, unknown> => {
  if (typeof structuredClone === "function") {
    return structuredClone(params);
  }

  try {
    return JSON.parse(JSON.stringify(params)) as Record<string, unknown>;
  } catch {
    return { ...params };
  }
};

const cloneOrGroupsWithNewIds = (groups: OrGroups): OrGroups =>
  groups.map((group) =>
    group.map((signal) => ({
      id: newId(),
      type: signal.type,
      params: cloneSignalParams(signal.params),
    }))
  );

const cloneTradeConditionsWithNewIds = (conditions: TradeCondition[]): TradeCondition[] =>
  conditions.map((condition) => {
    const cloned = (typeof structuredClone === "function"
      ? structuredClone(condition)
      : JSON.parse(JSON.stringify(condition))) as TradeCondition;
    cloned.id = newId();
    if (cloned.greekConditions) {
      cloned.greekConditions = cloned.greekConditions.map((gc) => ({ ...gc, id: newId() }));
    }
    if (cloned.dynamicConditions) {
      cloned.dynamicConditions = cloned.dynamicConditions.map((dc) => ({ ...dc, id: newId() }));
    }
    return cloned;
  });

// Known invalid signal types that should be filtered out
const INVALID_SIGNAL_TYPES = new Set(["Option", "Stock", "Future"]);
const INTRADAY_ALLOWED_SIGNAL_TYPES = [
  "intraday_ma_crossover",
  "on_schedule",
  "dollar_return_cap",
  "percentage_return_cap",
];
const INTRADAY_ALLOWED_SIGNAL_TYPE_SET = new Set(INTRADAY_ALLOWED_SIGNAL_TYPES);
// Intraday strategy-level allow-lists (see signal_engine/signals/config.rs validate_intraday_signal_config).
const INTRADAY_STRATEGY_ENTRY_ALLOWED_TYPES = ["on_schedule"];
const INTRADAY_STRATEGY_ENTRY_ALLOWED_SET = new Set(INTRADAY_STRATEGY_ENTRY_ALLOWED_TYPES);
const INTRADAY_STRATEGY_EXIT_ALLOWED_TYPES = ["on_schedule"];
const INTRADAY_STRATEGY_EXIT_ALLOWED_SET = new Set(INTRADAY_STRATEGY_EXIT_ALLOWED_TYPES);
// Intraday leg-level allow-list matches the full intraday signal set (on_exit is not permitted anywhere for intraday).
const INTRADAY_LEG_EXIT_ALLOWED_TYPES = INTRADAY_ALLOWED_SIGNAL_TYPES;
const INTRADAY_LEG_EXIT_ALLOWED_SET = new Set(INTRADAY_LEG_EXIT_ALLOWED_TYPES);
const INTRADAY_EXCLUSIVE_SIGNAL_TYPES = new Set([
  "intraday_ma_crossover",
  "on_schedule",
]);
const NON_INTRADAY_SIGNAL_TYPES = new Set(
  Object.keys(SIGNAL_REGISTRY).filter((signalType) => !INTRADAY_EXCLUSIVE_SIGNAL_TYPES.has(signalType))
);
const NON_INTRADAY_ALLOWED_SIGNAL_TYPES = Array.from(NON_INTRADAY_SIGNAL_TYPES);
const STRATEGY_TRADE_SUCCESS_MESSAGE =
  "Your algorithmic strategy order has been successfully scheduled and will execute automatically as configured";

// Sanitize OrGroups to remove invalid signal types
const sanitizeOrGroups = (groups: OrGroups, allowedSignalTypes?: Set<string>): OrGroups => {
  return groups
    .map(andGroup => andGroup.filter(signal =>
      !INVALID_SIGNAL_TYPES.has(signal.type) &&
      (!allowedSignalTypes || allowedSignalTypes.has(signal.type))
    ))
    .filter(andGroup => andGroup.length > 0);
};

// Sanitize legs to remove invalid signals
const sanitizeLegs = (legs: Leg[], allowedSignalTypes?: Set<string>): Leg[] => {
  return legs.map(leg => ({
    ...leg,
    entryRules: sanitizeOrGroups(leg.entryRules, allowedSignalTypes),
    exitRules: sanitizeOrGroups(leg.exitRules, allowedSignalTypes),
  }));
};

const sanitizeOrGroupsForIntraday = (groups: OrGroups): OrGroups =>
  sanitizeOrGroups(groups, INTRADAY_ALLOWED_SIGNAL_TYPE_SET);
const sanitizeOrGroupsForIntradayStrategyEntry = (groups: OrGroups): OrGroups =>
  sanitizeOrGroups(groups, INTRADAY_STRATEGY_ENTRY_ALLOWED_SET);
const sanitizeOrGroupsForIntradayStrategyExit = (groups: OrGroups): OrGroups =>
  sanitizeOrGroups(groups, INTRADAY_STRATEGY_EXIT_ALLOWED_SET);
const sanitizeLegsForIntradayLegContext = (legs: Leg[]): Leg[] =>
  legs.map((leg) => ({
    ...leg,
    entryRules: sanitizeOrGroups(leg.entryRules, INTRADAY_ALLOWED_SIGNAL_TYPE_SET),
    exitRules: sanitizeOrGroups(leg.exitRules, INTRADAY_LEG_EXIT_ALLOWED_SET),
  }));
const sanitizeOrGroupsForNonIntraday = (groups: OrGroups): OrGroups =>
  groups
    .map(andGroup => andGroup.filter(signal =>
      !INVALID_SIGNAL_TYPES.has(signal.type) &&
      !INTRADAY_EXCLUSIVE_SIGNAL_TYPES.has(signal.type)
    ))
    .filter(andGroup => andGroup.length > 0);
const sanitizeLegsForNonIntraday = (legs: Leg[]): Leg[] => {
  return legs.map(leg => ({
    ...leg,
    entryRules: sanitizeOrGroupsForNonIntraday(leg.entryRules),
    exitRules: sanitizeOrGroupsForNonIntraday(leg.exitRules),
  }));
};

const normalizeTradeConditionForIntraday = (condition: TradeCondition): TradeCondition => ({
  ...condition,
  instrumentType: "Stock",
  optionType: undefined,
  strike: undefined,
  expirationMode: undefined,
  expiration: undefined,
  dte: undefined,
  impliedVol: undefined,
  delta: undefined,
  gamma: undefined,
  theta: undefined,
  vega: undefined,
  rho: undefined,
  greek: undefined,
  dynamicGreek: undefined,
  greekValue: undefined,
  greekConditions: undefined,
});

const normalizeLegsForIntraday = (legs: Leg[]): Leg[] =>
  legs.map((leg) => ({
    ...leg,
    entryRules: sanitizeOrGroupsForIntraday(leg.entryRules),
    exitRules: sanitizeOrGroupsForIntraday(leg.exitRules),
    tradeConditions: leg.tradeConditions.map(normalizeTradeConditionForIntraday),
  }));

const normalizeSymbolBank = (
  symbols?: string[],
  knownSymbolSet?: Set<string> | null,
): string[] => {
  if (!symbols || symbols.length === 0) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const rawSymbol of symbols) {
    const symbol = (rawSymbol || "").trim().toUpperCase();
    if (!symbol || seen.has(symbol)) continue;
    if (knownSymbolSet && !knownSymbolSet.has(symbol)) continue;
    seen.add(symbol);
    normalized.push(symbol);
  }

  return normalized;
};

const sanitizeLegConditionSymbols = (
  legs: Leg[],
  knownSymbolSet?: Set<string> | null,
): Leg[] => {
  return legs.map((leg) => ({
    ...leg,
    tradeConditions: leg.tradeConditions.map((condition) => {
      const normalized = (condition.symbol || "").trim().toUpperCase();
      if (!normalized) {
        return condition.symbol ? { ...condition, symbol: "" } : condition;
      }
      if (knownSymbolSet && !knownSymbolSet.has(normalized)) {
        return { ...condition, symbol: "" };
      }
      return condition.symbol === normalized ? condition : { ...condition, symbol: normalized };
    }),
  }));
};

const formatContextLabel = (context: StrategyTicketContext): string => {
  if (context === "trading") return "Trading Monitor";
  return "Chat";
};

// Create an empty signal
const createEmptySignal = (): ConfiguredSignal => ({
  id: generateId(),
  type: "",
  params: {},
});

const hasConfiguredSignal = (groups: OrGroups): boolean =>
  groups.some((group) =>
    group.some((signal) => typeof signal.type === "string" && signal.type.trim() !== "")
  );

const normalizeStrategyNameForUniqueness = (value: string): string =>
  value.trim().toLowerCase();

const extractApiErrorMessage = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) return null;
  const data = (response as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  const message = (data as { error?: unknown }).error;
  return typeof message === "string" && message.trim().length > 0 ? message : null;
};

interface BacktestConfigProps {
  sessionId?: string;
  context?: StrategyTicketContext;
  persistenceScope?: string;
}

interface ApplyBacktestConfigDetail {
  config: BacktestRequest;
  configId?: string;
  sourceTriggerMessageId?: string;
}

interface BacktestConfigPersistedState {
  legs: Leg[];
  symbolBank: string[];
  intradayEnabled: boolean;
  strategyName: string;
  startDate: string;
  endDate: string;
  strategyEntryRules: OrGroups;
  strategyExitRules: OrGroups;
  nameValidationError: string | null;
  hasDuplicateName: boolean;
  isCheckingName: boolean;
  submitError: string | null;
  tradeNotice: string | null;
  isTradeSubmitting: boolean;
  isTradeConfirmOpen: boolean;
  tradeConfirmConfig: BacktestRequest | null;
  tradeConfirmError: string | null;
  symbolModeNotice: string | null;
  sourceBacktestConfigId: string | null;
  sourceTriggerMessageId: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const asStringOrNull = (value: unknown): string | null => (typeof value === "string" ? value : null);

const buildDefaultBacktestConfigState = (): BacktestConfigPersistedState => ({
  legs: [],
  symbolBank: [],
  intradayEnabled: false,
  strategyName: "Strategy",
  startDate: "",
  endDate: "",
  strategyEntryRules: [],
  strategyExitRules: [],
  nameValidationError: null,
  hasDuplicateName: false,
  isCheckingName: false,
  submitError: null,
  tradeNotice: null,
  isTradeSubmitting: false,
  isTradeConfirmOpen: false,
  tradeConfirmConfig: null,
  tradeConfirmError: null,
  symbolModeNotice: null,
  sourceBacktestConfigId: null,
  sourceTriggerMessageId: null,
});

export const BacktestConfig: React.FC<BacktestConfigProps> = ({
  sessionId,
  context = 'chat',
  persistenceScope,
}) => {
  const { getToken, isAuthenticated, userUuid } = useAuth();
  const { createSession, currentSession } = useGlobalSession();
  const { registerJob, unregisterJob } = useJobs();
  const orderTicketNavigation = useOptionalOrderTicketNavigation();

  // Resolve the chat session this backtest belongs to. The New Chat page writes the
  // real session id into the URL via history.replaceState, which React Router — and
  // thus the sessionId prop — does not observe; so read window.location directly,
  // then fall back to the shared session context. Only when all are empty (truly no
  // active chat) does the caller create a session. Logged for diagnosis.
  const resolveActiveSessionId = useCallback((): string | undefined => {
    const urlSessionId = detectSessionIdFromPath(window.location.pathname);
    const resolved = sessionId ?? urlSessionId ?? currentSession?.id;
    logger.info('🧭 [BacktestConfig] Resolved backtest session', {
      propSessionId: sessionId,
      urlSessionId,
      contextSessionId: currentSession?.id,
      resolved,
    });
    return resolved;
  }, [sessionId, currentSession?.id]);

  const [legs, setLegs] = useState<Leg[]>([]);
  const [symbolBank, setSymbolBank] = useState<string[]>([]);
  const [intradayEnabled, setIntradayEnabled] = useState(false);
  const [strategyName, setStrategyName] = useState("Strategy");
  const [nameValidationError, setNameValidationError] = useState<string | null>(null);
  const [hasDuplicateName, setHasDuplicateName] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [sourceBacktestConfigId, setSourceBacktestConfigId] = useState<string | null>(null);
  const [sourceTriggerMessageId, setSourceTriggerMessageId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Inline error states
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tradeNotice, setTradeNotice] = useState<string | null>(null);
  const [isTradeSubmitting, setIsTradeSubmitting] = useState(false);
  const [isTradeConfirmOpen, setIsTradeConfirmOpen] = useState(false);
  const [isTradeSuccessOpen, setIsTradeSuccessOpen] = useState(false);
  const [tradeConfirmConfig, setTradeConfirmConfig] = useState<BacktestRequest | null>(null);
  const [tradeConfirmError, setTradeConfirmError] = useState<string | null>(null);
  const [symbolModeNotice, setSymbolModeNotice] = useState<string | null>(null);
  const [knownSymbolSet, setKnownSymbolSet] = useState<Set<string> | null>(null);
  const [hasHydratedPersistence, setHasHydratedPersistence] = useState(false);
  const tradeNoticeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadStockUniverse()
      .then((universe) => {
        if (cancelled) return;
        setKnownSymbolSet(new Set(Object.keys(universe.stocks)));
      })
      .catch(() => {
        if (cancelled) return;
        setKnownSymbolSet(new Set(Object.keys(FALLBACK_STOCK_UNIVERSE_DATA.stocks)));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const capabilities = useMemo(() => {
    const isChat = context === "chat";
    return {
      showDateRange: isChat,
      symbolMode: isChat ? "multi" as const : "single" as const,
      contextLabel: formatContextLabel(context),
    };
  }, [context]);
  const isSingleSymbolMode = capabilities.symbolMode === "single";
  const strategyStorageKey = useMemo(
    () => (persistenceScope ? buildStrategyOrderStorageKey(persistenceScope) : null),
    [persistenceScope]
  );

  // Strategy-wide signals
  const [strategyEntryRules, setStrategyEntryRules] = useState<OrGroups>([]);
  const [strategyExitRules, setStrategyExitRules] = useState<OrGroups>([]);
  const [copiedSignal, setCopiedSignal] = useState<CopiedSignalPayload | null>(null);
  const [copiedLeg, setCopiedLeg] = useState<CopiedLegPayload | null>(null);

  const handleCopySignal = useCallback((payload: CopiedSignalPayload) => {
    setCopiedSignal({
      type: payload.type,
      params: cloneSignalParams(payload.params),
    });
  }, []);

  const handleCopyLeg = useCallback((leg: Leg) => {
    setCopiedLeg({
      entryRules: cloneOrGroupsWithNewIds(leg.entryRules),
      exitRules: cloneOrGroupsWithNewIds(leg.exitRules),
      tradeConditions: cloneTradeConditionsWithNewIds(leg.tradeConditions),
    });
  }, []);

  const applyPersistedState = useCallback((next: BacktestConfigPersistedState) => {
    setLegs(next.legs);
    setSymbolBank(next.symbolBank);
    setIntradayEnabled(next.intradayEnabled);
    setStrategyName(next.strategyName);
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    setStrategyEntryRules(next.strategyEntryRules);
    setStrategyExitRules(next.strategyExitRules);
    setNameValidationError(next.nameValidationError);
    setHasDuplicateName(next.hasDuplicateName);
    setIsCheckingName(next.isCheckingName);
    setSubmitError(next.submitError);
    setTradeNotice(next.tradeNotice);
    setIsTradeSubmitting(next.isTradeSubmitting);
    setIsTradeConfirmOpen(next.isTradeConfirmOpen);
    setTradeConfirmConfig(next.tradeConfirmConfig);
    setTradeConfirmError(next.tradeConfirmError);
    setSymbolModeNotice(next.symbolModeNotice);
    setSourceBacktestConfigId(next.sourceBacktestConfigId);
    setSourceTriggerMessageId(next.sourceTriggerMessageId);
  }, []);

  const resolvePersistedState = useCallback((raw: unknown): BacktestConfigPersistedState => {
    const defaults = buildDefaultBacktestConfigState();
    if (!isRecord(raw)) return defaults;

    const intradayEnabledValue = raw.intradayEnabled === true;
    const rawLegs = Array.isArray(raw.legs) ? raw.legs as Leg[] : defaults.legs;
    const sanitizedLegs = sanitizeLegConditionSymbols(
      intradayEnabledValue
        ? sanitizeLegs(rawLegs, INTRADAY_ALLOWED_SIGNAL_TYPE_SET)
        : sanitizeLegsForNonIntraday(rawLegs),
      knownSymbolSet
    );
    const legsValue = intradayEnabledValue ? normalizeLegsForIntraday(sanitizedLegs) : sanitizedLegs;
    const loadedSymbols = normalizeSymbolBank(
      Array.isArray(raw.symbolBank) ? raw.symbolBank as string[] : defaults.symbolBank,
      knownSymbolSet
    );
    const firstLoadedSymbol = loadedSymbols[0];
    const hasMultipleLoadedSymbols = loadedSymbols.length > 1;
    const symbolBankValue = isSingleSymbolMode
      ? (firstLoadedSymbol ? [firstLoadedSymbol] : [])
      : loadedSymbols;

    const strategyEntryRulesValue = (
      intradayEnabledValue ? sanitizeOrGroupsForIntraday : sanitizeOrGroupsForNonIntraday
    )(
      Array.isArray(raw.strategyEntryRules) ? raw.strategyEntryRules as OrGroups : defaults.strategyEntryRules
    );
    const strategyExitRulesValue = (
      intradayEnabledValue ? sanitizeOrGroupsForIntraday : sanitizeOrGroupsForNonIntraday
    )(
      Array.isArray(raw.strategyExitRules) ? raw.strategyExitRules as OrGroups : defaults.strategyExitRules
    );

    const tradeConfirmConfig = isRecord(raw.tradeConfirmConfig)
      ? raw.tradeConfirmConfig as unknown as BacktestRequest
      : null;

    return {
      legs: legsValue,
      symbolBank: symbolBankValue,
      intradayEnabled: intradayEnabledValue,
      strategyName: typeof raw.strategyName === "string" ? raw.strategyName : defaults.strategyName,
      startDate: typeof raw.startDate === "string" ? raw.startDate : defaults.startDate,
      endDate: typeof raw.endDate === "string" ? raw.endDate : defaults.endDate,
      strategyEntryRules: strategyEntryRulesValue,
      strategyExitRules: strategyExitRulesValue,
      nameValidationError: asStringOrNull(raw.nameValidationError),
      hasDuplicateName: raw.hasDuplicateName === true,
      isCheckingName: raw.isCheckingName === true,
      submitError: asStringOrNull(raw.submitError),
      tradeNotice: asStringOrNull(raw.tradeNotice),
      isTradeSubmitting: raw.isTradeSubmitting === true,
      isTradeConfirmOpen: raw.isTradeConfirmOpen === true && Boolean(tradeConfirmConfig),
      tradeConfirmConfig,
      tradeConfirmError: asStringOrNull(raw.tradeConfirmError),
      symbolModeNotice: isSingleSymbolMode && hasMultipleLoadedSymbols && firstLoadedSymbol
        ? `Multiple symbols were provided. Using ${firstLoadedSymbol} for ${capabilities.contextLabel}.`
        : asStringOrNull(raw.symbolModeNotice),
      sourceBacktestConfigId: asStringOrNull(raw.sourceBacktestConfigId),
      sourceTriggerMessageId: asStringOrNull(raw.sourceTriggerMessageId),
    };
  }, [capabilities.contextLabel, isSingleSymbolMode, knownSymbolSet]);

  const resetPersistedState = useCallback(() => {
    const defaults = resolvePersistedState(buildDefaultBacktestConfigState());
    if (strategyStorageKey) {
      removeSessionStorageKey(strategyStorageKey);
    }
    applyPersistedState(defaults);
    setHasHydratedPersistence(true);
  }, [applyPersistedState, resolvePersistedState, strategyStorageKey]);

  /**
   * Apply an AI-generated backtest config to the UI state
   * This is called when receiving a backtest_config from the backend
   */
  const applyBacktestConfig = useCallback(
    (
      config: BacktestRequest,
      configId?: string,
      incomingSourceTriggerMessageId?: string
    ) => {
      logger.info('🎨 [UI RENDER] Backtest config received, rendering UI:', JSON.stringify(config, null, 2));
      const parsed = parseBacktestConfig(config);
      const parsedSymbols = normalizeSymbolBank(parsed.symbolBank, knownSymbolSet);
      const singleSymbol = parsedSymbols[0];
      const hasMultipleSymbols = parsedSymbols.length > 1;
      const nextSymbolBank = isSingleSymbolMode
        ? (singleSymbol ? [singleSymbol] : [])
        : parsedSymbols;

      const sanitizedLegs = sanitizeLegConditionSymbols(
        intradayEnabled
          ? sanitizeLegs(parsed.legs, INTRADAY_ALLOWED_SIGNAL_TYPE_SET)
          : sanitizeLegsForNonIntraday(parsed.legs),
        knownSymbolSet
      );
      const nextLegs = intradayEnabled
        ? normalizeLegsForIntraday(sanitizedLegs)
        : sanitizedLegs;
      const nextStrategyEntryRules = intradayEnabled
        ? sanitizeOrGroupsForIntraday(parsed.strategyEntryRules)
        : sanitizeOrGroupsForNonIntraday(parsed.strategyEntryRules);
      const nextStrategyExitRules = intradayEnabled
        ? sanitizeOrGroupsForIntraday(parsed.strategyExitRules)
        : sanitizeOrGroupsForNonIntraday(parsed.strategyExitRules);

      setLegs(nextLegs);
      setSymbolBank(nextSymbolBank);
      setStrategyName(parsed.strategyName);
      setStartDate(parsed.startDate);
      setEndDate(parsed.endDate);
      setStrategyEntryRules(nextStrategyEntryRules);
      setStrategyExitRules(nextStrategyExitRules);
      setSymbolModeNotice(
        isSingleSymbolMode && hasMultipleSymbols && singleSymbol
          ? `Multiple symbols were provided. Using ${singleSymbol} for ${capabilities.contextLabel}.`
          : null
      );

      setSourceBacktestConfigId(configId ?? null);
      setSourceTriggerMessageId(incomingSourceTriggerMessageId ?? null);
    },
    [intradayEnabled, isSingleSymbolMode, capabilities.contextLabel, knownSymbolSet]
  );

  // Expose applyBacktestConfig for parent components (via ref or context)
  // For now, we'll use a window event for simplicity
  useEffect(() => {
    const handleApplyConfig = (event: CustomEvent<ApplyBacktestConfigDetail>) => {
      // Support both old format (just config) and new format (config + configId)
      const detail = event.detail;
      if ('config' in detail) {
        applyBacktestConfig(detail.config, detail.configId, detail.sourceTriggerMessageId);
      } else {
        // Legacy format - just the config object
        applyBacktestConfig(detail as unknown as BacktestRequest);
      }
    };

    window.addEventListener('applyBacktestConfig', handleApplyConfig as EventListener);
    return () => {
      window.removeEventListener('applyBacktestConfig', handleApplyConfig as EventListener);
    };
  }, [applyBacktestConfig]);

  useEffect(() => {
    if (!hasHydratedPersistence) {
      return;
    }

    const normalizedName = strategyName.trim().toLowerCase();

    if (!normalizedName) {
      setHasDuplicateName(false);
      setNameValidationError('Strategy name is required');
      setIsCheckingName(false);
      return;
    }

    // When no persisted session exists yet, this is effectively a new namespace.
    if (!sessionId || !isAuthenticated || !userUuid) {
      setHasDuplicateName(false);
      setNameValidationError(null);
      setIsCheckingName(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setIsCheckingName(true);
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const exists = await backtestService.checkBacktestNameExists(
          sessionId,
          normalizedName,
          userUuid,
          token
        );

        if (cancelled) return;

        setHasDuplicateName(exists);
        setNameValidationError(
          exists
            ? 'This name is already used. Please choose a different name.'
            : null
        );
      } catch (error) {
        if (cancelled) return;
        logger.warn('Failed to check backtest name availability', error);
        setHasDuplicateName(false);
        setNameValidationError(null);
      } finally {
        if (!cancelled) {
          setIsCheckingName(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [hasHydratedPersistence, strategyName, sessionId, isAuthenticated, userUuid, getToken]);

  // Symbol bank functions
  const addSymbols = (symbols: string[]) =>
    setSymbolBank((prev) => {
      const seen = new Set(prev);
      const next = [...prev];

      for (const rawSymbol of symbols) {
        const normalized = rawSymbol.trim().toUpperCase();
        if (!normalized || seen.has(normalized)) {
          continue;
        }
        if (knownSymbolSet && !knownSymbolSet.has(normalized)) {
          continue;
        }
        seen.add(normalized);
        next.push(normalized);
      }

      if (next.length === prev.length) {
        return prev;
      }

      return next;
    });

  const addSymbol = (symbol: string) => addSymbols([symbol]);

  const clearSymbols = () => setSymbolBank([]);

  const removeSymbol = (idx: number) =>
    setSymbolBank((prev) => prev.filter((_, i) => i !== idx));

  const setSingleSymbol = useCallback((rawSymbol: string) => {
    const normalized = rawSymbol.trim().toUpperCase();
    const nextSymbol = normalized && (!knownSymbolSet || knownSymbolSet.has(normalized))
      ? normalized
      : "";
    setSymbolBank(nextSymbol ? [nextSymbol] : []);
    setSymbolModeNotice(null);
  }, [knownSymbolSet]);

  // Leg management
  const addLeg = () =>
    setLegs((prev) => [
      ...prev,
      {
        id: newId(),
        name: `Leg ${prev.length + 1}`,
        entryRules: [],  // OrGroups: start empty, user clicks Add to add signals
        tradeConditions: [{
          id: newId(),
          instrumentType: 'Stock',
          quantity: 1,
          direction: 'long'
        }],
        exitRules: [],   // OrGroups: start empty, user clicks Add to add signals
      },
    ]);

  const removeLeg = (id: string) => setLegs(legs.filter((l) => l.id !== id));

  // Helper to mutate a specific leg
  const mutateLeg = (legId: string, mutator: (leg: Leg) => Leg) =>
    setLegs((prev) => prev.map((leg) => (leg.id === legId ? mutator(leg) : leg)));

  const handlePasteLeg = (legId: string) => {
    if (!copiedLeg) return;
    mutateLeg(legId, (l) => ({
      ...l,
      entryRules: cloneOrGroupsWithNewIds(copiedLeg.entryRules),
      exitRules: cloneOrGroupsWithNewIds(copiedLeg.exitRules),
      tradeConditions: cloneTradeConditionsWithNewIds(copiedLeg.tradeConditions),
    }));
  };

  // Entry rules - now with groupIdx for AndGroup and signalIdx for signal within group
  const addEntrySignal = (legId: string, groupIdx: number) =>
    mutateLeg(legId, (l) => ({
      ...l,
      entryRules: l.entryRules.map((group, i) =>
        i === groupIdx ? [...group, createEmptySignal()] : group
      ),
    }));

  const removeEntrySignal = (legId: string, groupIdx: number, signalIdx: number) =>
    mutateLeg(legId, (l) => ({
      ...l,
      entryRules: l.entryRules.map((group, i) =>
        i === groupIdx ? group.filter((_, j) => j !== signalIdx) : group
      ),
    }));

  const updateEntrySignal = (legId: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) =>
    mutateLeg(legId, (l) => ({
      ...l,
      entryRules: l.entryRules.map((group, i) =>
        i === groupIdx ? group.map((s, j) => (j === signalIdx ? signal : s)) : group
      ),
    }));

  const addEntryGroup = (legId: string) =>
    mutateLeg(legId, (l) => ({
      ...l,
      entryRules: [...l.entryRules, [createEmptySignal()]],
    }));

  const removeEntryGroup = (legId: string, groupIdx: number) =>
    mutateLeg(legId, (l) => ({
      ...l,
      entryRules: l.entryRules.filter((_, i) => i !== groupIdx),
    }));

  // Trade conditions
  const updateTradeConditionInLeg = (legId: string, idx: number, field: keyof TradeCondition, value: any) =>
    mutateLeg(legId, (l) => {
      const updated = [...l.tradeConditions];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...l, tradeConditions: updated };
    });

  // Exit rules - now with groupIdx for AndGroup and signalIdx for signal within group
  const addExitSignal = (legId: string, groupIdx: number) =>
    mutateLeg(legId, (l) => ({
      ...l,
      exitRules: l.exitRules.map((group, i) =>
        i === groupIdx ? [...group, createEmptySignal()] : group
      ),
    }));

  const removeExitSignal = (legId: string, groupIdx: number, signalIdx: number) =>
    mutateLeg(legId, (l) => ({
      ...l,
      exitRules: l.exitRules.map((group, i) =>
        i === groupIdx ? group.filter((_, j) => j !== signalIdx) : group
      ),
    }));

  const updateExitSignal = (legId: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) =>
    mutateLeg(legId, (l) => ({
      ...l,
      exitRules: l.exitRules.map((group, i) =>
        i === groupIdx ? group.map((s, j) => (j === signalIdx ? signal : s)) : group
      ),
    }));

  const addExitGroup = (legId: string) =>
    mutateLeg(legId, (l) => ({
      ...l,
      exitRules: [...l.exitRules, [createEmptySignal()]],
    }));

  const removeExitGroup = (legId: string, groupIdx: number) =>
    mutateLeg(legId, (l) => ({
      ...l,
      exitRules: l.exitRules.filter((_, i) => i !== groupIdx),
    }));

  // Strategy-wide signal handlers
  const addStrategyEntrySignal = (_: string, groupIdx: number) =>
    setStrategyEntryRules(prev => prev.map((group, i) =>
      i === groupIdx ? [...group, createEmptySignal()] : group
    ));

  const removeStrategyEntrySignal = (_: string, groupIdx: number, signalIdx: number) =>
    setStrategyEntryRules(prev => prev.map((group, i) =>
      i === groupIdx ? group.filter((_, j) => j !== signalIdx) : group
    ));

  const updateStrategyEntrySignal = (_: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) =>
    setStrategyEntryRules(prev => prev.map((group, i) =>
      i === groupIdx ? group.map((s, j) => (j === signalIdx ? signal : s)) : group
    ));

  const addStrategyEntryGroup = () =>
    setStrategyEntryRules(prev => [...prev, [createEmptySignal()]]);

  const removeStrategyEntryGroup = (_: string, groupIdx: number) =>
    setStrategyEntryRules(prev => prev.filter((_, i) => i !== groupIdx));

  const addStrategyExitSignal = (_: string, groupIdx: number) =>
    setStrategyExitRules(prev => prev.map((group, i) =>
      i === groupIdx ? [...group, createEmptySignal()] : group
    ));

  const removeStrategyExitSignal = (_: string, groupIdx: number, signalIdx: number) =>
    setStrategyExitRules(prev => prev.map((group, i) =>
      i === groupIdx ? group.filter((_, j) => j !== signalIdx) : group
    ));

  const updateStrategyExitSignal = (_: string, groupIdx: number, signalIdx: number, signal: ConfiguredSignal) =>
    setStrategyExitRules(prev => prev.map((group, i) =>
      i === groupIdx ? group.map((s, j) => (j === signalIdx ? signal : s)) : group
    ));

  const addStrategyExitGroup = () =>
    setStrategyExitRules(prev => [...prev, [createEmptySignal()]]);

  const removeStrategyExitGroup = (_: string, groupIdx: number) =>
    setStrategyExitRules(prev => prev.filter((_, i) => i !== groupIdx));

  const effectiveSymbolBank = useMemo(() => {
    const normalized = normalizeSymbolBank(symbolBank, knownSymbolSet);
    if (!isSingleSymbolMode) return normalized;
    const first = normalized[0];
    return first ? [first] : [];
  }, [symbolBank, knownSymbolSet, isSingleSymbolMode]);

  const selectedSingleSymbol = effectiveSymbolBank[0] || "";

  const effectiveLegs = useMemo(() => {
    const normalizedLegs = sanitizeLegConditionSymbols(legs, knownSymbolSet);
    if (!isSingleSymbolMode) return normalizedLegs;
    const forcedSymbol = selectedSingleSymbol;
    return normalizedLegs.map((leg) => ({
      ...leg,
      tradeConditions: leg.tradeConditions.map((condition) => ({
        ...condition,
        symbol: forcedSymbol || "",
      })),
    }));
  }, [isSingleSymbolMode, legs, knownSymbolSet, selectedSingleSymbol]);

  const effectiveStartDate = capabilities.showDateRange ? startDate : "";
  const effectiveEndDate = capabilities.showDateRange ? endDate : "";

  // Build UI state for validation and generation
  const uiState: UIBacktestState = useMemo(() => ({
    legs: effectiveLegs,
    symbolBank: effectiveSymbolBank,
    strategyName,
    startDate: effectiveStartDate,
    endDate: effectiveEndDate,
    intradayEnabled,
    strategySignalsEnabled: true,
    strategyEntryRules,
    strategyExitRules,
  }), [
    effectiveLegs,
    effectiveSymbolBank,
    strategyName,
    effectiveStartDate,
    effectiveEndDate,
    intradayEnabled,
    strategyEntryRules,
    strategyExitRules
  ]);

  // Validate config whenever state changes
  const validationResult = useMemo(() => validateBacktestConfig(uiState), [uiState]);
  const hasStrategyEntrySignal = useMemo(
    () => hasConfiguredSignal(strategyEntryRules),
    [strategyEntryRules]
  );
  const canBacktest = context === 'chat';
  const missingSingleSymbol = isSingleSymbolMode && selectedSingleSymbol.length === 0;
  const sharedValidationErrors = useMemo<ValidationError[]>(() => {
    if (!missingSingleSymbol) return validationResult.errors;
    return [
      {
        path: "Strategy Symbol",
        message: "Select a symbol for Strategy Order Ticket on this page.",
      },
      ...validationResult.errors,
    ];
  }, [missingSingleSymbol, validationResult.errors]);
  const backtestOnlyValidationErrors = useMemo<ValidationError[]>(() => {
    if (!canBacktest || intradayEnabled) {
      return [];
    }

    const errors: ValidationError[] = [];

    if (capabilities.showDateRange) {
      if (!effectiveStartDate || !effectiveEndDate) {
        errors.push({
          path: "Date Range",
          message: "Select both start and end dates before backtesting.",
        });
      } else {
        const start = new Date(effectiveStartDate);
        const end = new Date(effectiveEndDate);
        if (start >= end) {
          errors.push({
            path: "Date Range",
            message: "Start date must be before end date for backtesting.",
          });
        }
      }
    }

    if (!hasStrategyEntrySignal) {
      errors.push({
        path: "Strategy.Entry",
        message: "Select a strategy entry signal before backtesting.",
      });
    }

    return errors;
  }, [
    canBacktest,
    intradayEnabled,
    capabilities.showDateRange,
    effectiveStartDate,
    effectiveEndDate,
    hasStrategyEntrySignal,
  ]);
  const tradeValidationErrors = sharedValidationErrors;
  const backtestValidationErrors = useMemo<ValidationError[]>(
    () => [...sharedValidationErrors, ...backtestOnlyValidationErrors],
    [sharedValidationErrors, backtestOnlyValidationErrors]
  );
  const shouldSuppressStrategyEntryWarning =
    canBacktest && !intradayEnabled && !hasStrategyEntrySignal;
  const displayValidationWarnings = useMemo(
    () =>
      shouldSuppressStrategyEntryWarning
        ? validationResult.warnings.filter((warning) => warning.path !== "Strategy.Entry")
        : validationResult.warnings,
    [shouldSuppressStrategyEntryWarning, validationResult.warnings]
  );
  const isTradeValid = tradeValidationErrors.length === 0;
  const isBacktestValid = backtestValidationErrors.length === 0;
  const trimmedStrategyName = strategyName.trim();
  const normalizedStrategyName = normalizeStrategyNameForUniqueness(trimmedStrategyName);
  
  // Check if any leg has options
  const hasOptionsInLegs = legs.some(leg => 
    leg.tradeConditions.some(condition => condition.instrumentType === 'Option')
  );
  
  const allowedSignalTypes = intradayEnabled
    ? [...INTRADAY_ALLOWED_SIGNAL_TYPES]
    : NON_INTRADAY_ALLOWED_SIGNAL_TYPES;
  const strategyEntryAllowedSignalTypes = intradayEnabled
    ? INTRADAY_STRATEGY_ENTRY_ALLOWED_TYPES
    : allowedSignalTypes;
  const strategyExitAllowedSignalTypes = intradayEnabled
    ? INTRADAY_STRATEGY_EXIT_ALLOWED_TYPES
    : allowedSignalTypes;
  const legEntryAllowedSignalTypes = allowedSignalTypes;
  const legExitAllowedSignalTypes = intradayEnabled
    ? INTRADAY_LEG_EXIT_ALLOWED_TYPES
    : allowedSignalTypes;
  const isTradeSubmitDisabled = !isTradeValid || trimmedStrategyName.length === 0 || isCheckingName || hasOptionsInLegs;
  const isTradeActionDisabled = isTradeSubmitDisabled || isTradeSubmitting;
  const isBacktestSubmitDisabled = !isBacktestValid || trimmedStrategyName.length === 0 || isCheckingName;
  const isBacktestDisabled =
    isBacktestSubmitDisabled || isTradeSubmitting || intradayEnabled;
  const backtestButtonTitle = intradayEnabled
    ? 'Backtest is unavailable when Intraday is enabled. Use Trade.'
    : !isBacktestValid
      ? 'Fix validation errors before backtesting'
      : trimmedStrategyName.length === 0
        ? 'Enter a strategy name before backtesting'
        : isCheckingName
            ? 'Checking strategy name availability'
            : hasDuplicateName
              ? 'Backtesting will ask whether to overwrite the existing strategy'
              : 'Generate and run strategy backtest';
  const tradeButtonTitle = hasOptionsInLegs
    ? 'Coming soon, trading not supported on Options yet'
    : !isTradeValid
      ? 'Fix validation errors before trading'
      : trimmedStrategyName.length === 0
        ? 'Enter a strategy name before trading'
        : isCheckingName
          ? 'Checking strategy name availability'
          : isTradeSubmitting
            ? 'Scheduling strategy'
            : 'Trade (schedules strategy)';

  useEffect(() => {
    if (!isSingleSymbolMode) {
      setSymbolModeNotice(null);
      return;
    }

    setStartDate("");
    setEndDate("");

    const normalized = normalizeSymbolBank(symbolBank, knownSymbolSet);
    const first = normalized[0];
    const nextSingle = first ? [first] : [];
    const needsNormalization =
      symbolBank.length !== nextSingle.length ||
      symbolBank.some((symbol, idx) => (nextSingle[idx] || "") !== symbol);

    if (needsNormalization) {
      setSymbolBank(nextSingle);
    }

    if (normalized.length > 1 && first) {
      setSymbolModeNotice(`Multiple symbols were provided. Using ${first} for ${capabilities.contextLabel}.`);
    }
  }, [isSingleSymbolMode, symbolBank, knownSymbolSet, capabilities.contextLabel]);

  useEffect(() => {
    if (intradayEnabled) {
      setLegs((prev) =>
        sanitizeLegsForIntradayLegContext(normalizeLegsForIntraday(prev)),
      );
      setStrategyEntryRules((prev) => sanitizeOrGroupsForIntradayStrategyEntry(prev));
      setStrategyExitRules((prev) => sanitizeOrGroupsForIntradayStrategyExit(prev));
      return;
    }

    setLegs((prev) => sanitizeLegsForNonIntraday(prev));
    setStrategyEntryRules((prev) => sanitizeOrGroupsForNonIntraday(prev));
    setStrategyExitRules((prev) => sanitizeOrGroupsForNonIntraday(prev));
  }, [intradayEnabled]);

  // Clear submit errors when user starts typing the name
  useEffect(() => {
    setSubmitError(null);
  }, [strategyName]);

  useEffect(() => {
    return () => {
      if (tradeNoticeTimeoutRef.current) {
        window.clearTimeout(tradeNoticeTimeoutRef.current);
      }
    };
  }, []);

  // Execute the actual backtest run.
  const executeBacktest = async () => {
    setSubmitError(null);

    const backtestRequest = generateBacktestRequest(uiState);

    // Pretty print the JSON to console
    logger.info('='.repeat(60));
    logger.info('Generated BacktestRequest JSON:');
    logger.info('='.repeat(60));
    logger.info(JSON.stringify(backtestRequest, null, 2));
    logger.info('='.repeat(60));

    // 0. Bind to the active chat session (URL via replaceState, else shared context)
    // so the run persists into the chat the user is in. Only create one when there is
    // genuinely no active session.
    let sessionIdToUse = resolveActiveSessionId();
    if (!sessionIdToUse && isAuthenticated && userUuid) {
      logger.info('📋 [BacktestConfig] No session - creating new session for UI-triggered backtest');
      try {
        const session = await createSession();
        sessionIdToUse = session.id;
        logger.info('✅ [BacktestConfig] Session created with ID:', sessionIdToUse);
      } catch (error) {
        logger.error('❌ Error creating session:', error);
        setSubmitError('Failed to create session. Please try again.');
        return;
      }
    }

    // Verify we have required auth info
    if (!sessionIdToUse || !isAuthenticated || !userUuid) {
      logger.error('❌ Missing required authentication info');
      setSubmitError('Authentication required. Please log in and try again.');
      return;
    }

    const token = await getToken();
    if (!token) {
      logger.error('❌ Missing authentication token');
      setSubmitError('Authentication token not available. Please try again.');
      return;
    }

    // 1. Close the side menu immediately
    window.dispatchEvent(new CustomEvent('closeSideConfigMenu'));
    logger.info('📂 [BacktestConfig] Dispatched closeSideConfigMenu event');

    // 2. Generate a stable progress message ID for this backtest run
    const progressId = `backtest-progress-${Date.now()}`;

    // Dispatch initial progress bar message (will be updated in place)
    window.dispatchEvent(new CustomEvent('backtestProgress', {
      detail: {
        progressId,
        progress: 0,
        phase: 'planning' as const,
        currentPeriod: undefined,
      }
    }));
    logger.info('📢 [BacktestConfig] Dispatched initial progress bar:', progressId);

    // 3. Call unified Python backend API that handles everything
    logger.info('🚀 Calling unified Python backend API...');
    let activeJobId: string | null = null;

    const handleProgress = (progress: BacktestProgress) => {
      // Submit-failure UX is assistant-first. We only show the red failed bar
      // in explicit persistence-fallback scenarios handled in catch().
      if (progress.phase === 'failed') {
        logger.info('⚠️ [BacktestConfig] Suppressing failed progress event in favor of assistant-first UX');
        return;
      }

      // Dispatch progress update - UI will update the same message in place
      window.dispatchEvent(new CustomEvent('backtestProgress', {
        detail: {
          progressId,
          progress: progress.overall_progress,
          phase: progress.phase,
          currentPeriod: progress.current_period,
          currentSymbol: progress.current_symbol,
          error: progress.error,
        }
      }));
    };

    // Callback to register job as soon as we get the job ID
    const handleJobId = (jobId: string) => {
      logger.info('📝 [BacktestConfig] Received job ID, registering:', jobId);
      activeJobId = jobId;
      registerJob(jobId, 'backtest', `Backtest: ${strategyName}`);
    };

    const handleAssistantMessage = ({ messageId, text }: AssistantMessagePayload) => {
      logger.info('💬 [BacktestConfig] Dispatching assistant failure message:', messageId);
      window.dispatchEvent(
        new CustomEvent('backtestAssistantMessage', {
          detail: {
            progressId,
            messageId,
            text,
          },
        })
      );
    };

    try {
      // Call the unified API that:
      // 1. Saves the config
      // 2. Runs the backtest with streaming
      // 3. Saves the results
      // 4. Creates chat messages
      const resultData = await backtestService.runBacktestWithStreaming(
        sessionIdToUse,
        backtestRequest,
        userUuid,
        token,
        handleProgress,
        handleJobId,
        normalizedStrategyName,
        sourceBacktestConfigId && sourceTriggerMessageId
          ? {
              sourceBacktestConfigId,
              sourceTriggerMessageId,
            }
          : undefined,
        handleAssistantMessage
      );

      logger.info('✅ Backtest completed successfully');

      // Update progress bar to complete state and include results for chart
      window.dispatchEvent(new CustomEvent('backtestProgress', {
        detail: {
          progressId,
          progress: 100,
          phase: 'complete' as const,
          results: resultData,  // Include backtest results for chart rendering
        }
      }));

      // Dispatch backtestResultSaved event so SessionChatPage/NewChatPage can
      // attach the backtest_result { backtest_config_id, results_data_id }
      // reference to the message for persistence and reuse via data_id.
      if (resultData?.backtestConfigId && resultData?.resultsDataId) {
        logger.info('💾 [BacktestConfig] Dispatching backtestResultSaved:', {
          backtestConfigId: resultData.backtestConfigId,
          resultsDataId: resultData.resultsDataId,
        });
        window.dispatchEvent(new CustomEvent('backtestResultSaved', {
          detail: {
            progressId,
            backtestConfigId: resultData.backtestConfigId,
            resultsDataId: resultData.resultsDataId,
          }
        }));
      }

      // Source AI context is single-use for submit replacement semantics.
      setSourceBacktestConfigId(null);
      setSourceTriggerMessageId(null);

      logger.info('✅ [BacktestConfig] Backtest execution and persistence complete');

    } catch (error) {
      logger.error('='.repeat(60));
      logger.error('❌ Backtest request error:', error);
      logger.error('='.repeat(60));
      const streamError = error as BacktestStreamingError;
      const assistantMessageReceived = streamError?.assistantMessageReceived === true;
      const isPersistenceFailure = streamError?.kind === 'failure_persistence_error';
      const isNameConflict = streamError?.kind === 'name_conflict';

      if (isNameConflict) {
        // The run never started — the name already exists. Drop the in-progress
        // bar, reopen the config menu, and prompt the user to choose a new name.
        logger.warn('⚠️ [BacktestConfig] Backtest name conflict; prompting for a different name');
        window.dispatchEvent(new CustomEvent('backtestProgressRemove', { detail: { progressId } }));
        window.dispatchEvent(new CustomEvent('openSideConfigMenu', { detail: { tab: 'backtest' } }));
        setHasDuplicateName(true);
        setNameValidationError(
          streamError.message || 'This name is already used. Please choose a different name.'
        );
        return;
      }

      if (assistantMessageReceived && !isPersistenceFailure) {
        logger.info('✅ [BacktestConfig] Assistant failure message persisted; skipping red failed progress bar');
      } else {
        const fallbackErrorText = isPersistenceFailure
          ? `Backtest failed and could not be saved as assistant message: ${streamError?.persistenceError || 'Unknown persistence error'}`
          : error instanceof Error
            ? error.message
            : 'Unknown error';

        // Fallback-only failed bar for persistence failure or legacy error paths.
        window.dispatchEvent(new CustomEvent('backtestProgress', {
          detail: {
            progressId,
            progress: 100,
            phase: 'failed' as const,
            error: fallbackErrorText,
          }
        }));
      }
    } finally {
      if (activeJobId) {
        unregisterJob(activeJobId);
      }
    }
  };

  // Handle submit - validate and check for name conflicts before executing
  const handleSubmit = async () => {
    // Clear any previous errors
    setSubmitError(null);

    if (intradayEnabled) {
      return;
    }

    if (!isBacktestValid) {
      logger.error('Backtest validation failed:', backtestValidationErrors);
      // Validation errors are already shown in the UI, just return
      return;
    }

    if (!normalizedStrategyName) {
      setNameValidationError('Strategy name is required');
      return;
    }

    // Check name uniqueness before proceeding. Bind to the active chat session (URL
    // via replaceState, else shared context). When no session exists at all, the
    // session is created inside executeBacktest and the authoritative backend check
    // still rejects duplicates via name_conflict.
    let sessionIdToUse = resolveActiveSessionId();
    if (!sessionIdToUse && isAuthenticated && userUuid) {
      await executeBacktest();
      return;
    }

    if (!sessionIdToUse || !isAuthenticated || !userUuid) {
      setSubmitError('Authentication required. Please log in and try again.');
      return;
    }

    const token = await getToken();
    if (!token) {
      setSubmitError('Authentication token not available. Please try again.');
      return;
    }

    try {
      const nameExists = await backtestService.checkBacktestNameExists(
        sessionIdToUse,
        normalizedStrategyName,
        userUuid,
        token
      );

      if (nameExists) {
        // Names must be unique — block and ask the user to choose a different one.
        setHasDuplicateName(true);
        setNameValidationError('This name is already used. Please choose a different name.');
        return;
      }

      // Name doesn't exist, proceed with backtest
      setHasDuplicateName(false);
      setNameValidationError(null);
      await executeBacktest();
    } catch (error) {
      logger.warn('⚠️ Could not validate backtest name uniqueness, proceeding anyway:', error);
      // Name-check is best-effort here; the backend still enforces uniqueness and
      // will surface a name_conflict if the name is taken.
      setHasDuplicateName(false);
      setNameValidationError(null);
      await executeBacktest();
    }
  };

  const showTradeNotice = useCallback((message: string) => {
    setTradeNotice(message);
    if (tradeNoticeTimeoutRef.current) {
      window.clearTimeout(tradeNoticeTimeoutRef.current);
    }
    tradeNoticeTimeoutRef.current = window.setTimeout(() => {
      setTradeNotice(null);
      tradeNoticeTimeoutRef.current = null;
    }, 3000);
  }, []);

  const closeTradeConfirmModal = useCallback(() => {
    if (isTradeSubmitting) return;
    setIsTradeConfirmOpen(false);
    setTradeConfirmConfig(null);
    setTradeConfirmError(null);
  }, [isTradeSubmitting]);
  const closeTradeSuccessModal = useCallback(() => {
    setIsTradeSuccessOpen(false);
  }, []);

  useEffect(() => {
    if (!isTradeConfirmOpen && !isTradeSuccessOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      if (isTradeConfirmOpen) {
        if (isTradeSubmitting) return;
        closeTradeConfirmModal();
        return;
      }
      if (isTradeSuccessOpen) {
        closeTradeSuccessModal();
      }
    };

    window.addEventListener("keydown", handleEscape, true);
    return () => {
      window.removeEventListener("keydown", handleEscape, true);
    };
  }, [
    closeTradeConfirmModal,
    closeTradeSuccessModal,
    isTradeConfirmOpen,
    isTradeSubmitting,
    isTradeSuccessOpen,
  ]);

  const handleTrade = async () => {
    if (!isTradeValid || !trimmedStrategyName || isCheckingName || isTradeSubmitting) return;
    if (!isAuthenticated || !userUuid) {
      showTradeNotice('Authentication required. Please log in and try again.');
      return;
    }

    const token = await getToken();
    if (!token) {
      showTradeNotice('Authentication token not available. Please try again.');
      return;
    }

    try {
      const [positionState, blotterRows] = await Promise.all([
        tradingService.getPositionState(userUuid, token),
        tradingService.listOrderBlotter(userUuid, token, 1_000),
      ]);

      const activeNames = new Set(
        Object.values(positionState.strategy_positions || {})
          .map((position) => normalizeStrategyNameForUniqueness(position.name || ""))
          .filter((name) => name.length > 0),
      );

      if (activeNames.has(normalizedStrategyName)) {
        const message = 'Strategy name already exists in active positions. Please choose a different name.';
        setNameValidationError(message);
        showTradeNotice(message);
        return;
      }

      const scheduledNames = new Set(
        blotterRows
          .filter((row) => row.entry_kind === 'strategy' && row.status === 'scheduled')
          .map((row) => normalizeStrategyNameForUniqueness(row.strategy_name || ""))
          .filter((name) => name.length > 0),
      );

      if (scheduledNames.has(normalizedStrategyName)) {
        const message = 'Strategy name already exists in scheduled strategies. Please choose a different name.';
        setNameValidationError(message);
        showTradeNotice(message);
        return;
      }
    } catch (error) {
      logger.error('[BacktestConfig] Failed checking strategy name uniqueness for trading:', error);
      const message = 'Unable to verify strategy name uniqueness. Please retry.';
      setNameValidationError(message);
      showTradeNotice(message);
      return;
    }

    setIsTradeSuccessOpen(false);
    setNameValidationError(null);
    setTradeConfirmError(null);
    setTradeConfirmConfig({
      ...generateBacktestRequest(uiState),
      intraday: intradayEnabled,
    });
    setIsTradeConfirmOpen(true);
  };

  const handleConfirmTrade = async () => {
    if (!tradeConfirmConfig || !trimmedStrategyName || isTradeSubmitting) return;
    if (!isAuthenticated || !userUuid) {
      const message = 'Authentication required. Please log in and try again.';
      setTradeConfirmError(message);
      showTradeNotice(message);
      return;
    }

    setIsTradeSubmitting(true);
    setTradeConfirmError(null);
    try {
      const token = await getToken();
      if (!token) {
        const message = 'Authentication token not available. Please try again.';
        setTradeConfirmError(message);
        showTradeNotice(message);
        return;
      }

      const scheduleRequest = {
        name: trimmedStrategyName,
        config: tradeConfirmConfig as unknown as Record<string, unknown>,
      };

      const scheduled = orderTicketNavigation
        ? await orderTicketNavigation.scheduleStrategy(scheduleRequest)
        : await tradingService.scheduleStrategy(userUuid, scheduleRequest, token);

      logger.info('[BacktestConfig] Strategy scheduled:', scheduled);
      setIsTradeConfirmOpen(false);
      setTradeConfirmConfig(null);
      setIsTradeSuccessOpen(true);
      if (orderTicketNavigation) {
        setTimeout(() => orderTicketNavigation.closeStrategyOrderTicket(), 700);
      }
    } catch (error) {
      logger.error('[BacktestConfig] Failed scheduling strategy:', error);
      let message = 'Failed to schedule strategy. Please retry.';
      const apiErrorMessage = extractApiErrorMessage(error);
      if (apiErrorMessage) {
        message = apiErrorMessage;
      }
      setTradeConfirmError(message);
      showTradeNotice(message);
    } finally {
      setIsTradeSubmitting(false);
    }
  };

  useEffect(() => {
    setHasHydratedPersistence(false);
    if (!knownSymbolSet) return;

    let raw = strategyStorageKey
      ? readSessionStorageJson<unknown>(strategyStorageKey)
      : null;

    let didMigrateLegacyKey = false;
    if (!raw && sessionId) {
      raw = readSessionStorageJson<unknown>(`backtest-config-${sessionId}`);
      didMigrateLegacyKey = Boolean(raw);
    }

    const resolved = resolvePersistedState(raw);
    applyPersistedState(resolved);

    if (strategyStorageKey && raw) {
      writeSessionStorageJson(strategyStorageKey, resolved);
      if (didMigrateLegacyKey && sessionId) {
        removeSessionStorageKey(`backtest-config-${sessionId}`);
      }
    }

    setHasHydratedPersistence(true);
  }, [
    knownSymbolSet,
    strategyStorageKey,
    sessionId,
    resolvePersistedState,
    applyPersistedState,
  ]);

  useEffect(() => {
    if (!strategyStorageKey || !hasHydratedPersistence) return;

    const data: BacktestConfigPersistedState = {
      legs: effectiveLegs,
      symbolBank: effectiveSymbolBank,
      intradayEnabled,
      strategyName,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
      strategyEntryRules,
      strategyExitRules,
      nameValidationError,
      hasDuplicateName,
      isCheckingName,
      submitError,
      tradeNotice,
      isTradeSubmitting,
      isTradeConfirmOpen,
      tradeConfirmConfig,
      tradeConfirmError,
      symbolModeNotice,
      sourceBacktestConfigId,
      sourceTriggerMessageId,
    };

    writeSessionStorageJson(strategyStorageKey, data);
  }, [
    strategyStorageKey,
    hasHydratedPersistence,
    effectiveLegs,
    effectiveSymbolBank,
    intradayEnabled,
    strategyName,
    effectiveStartDate,
    effectiveEndDate,
    strategyEntryRules,
    strategyExitRules,
    nameValidationError,
    hasDuplicateName,
    isCheckingName,
    submitError,
    tradeNotice,
    isTradeSubmitting,
    isTradeConfirmOpen,
    tradeConfirmConfig,
    tradeConfirmError,
    symbolModeNotice,
    sourceBacktestConfigId,
    sourceTriggerMessageId,
  ]);

  return (
    <>
      <div className={styles.container}>
      {/* Header with intraday toggle and dates */}
      <div className={styles.header}>
        <div className={styles.intradayToggleRow}>
          <span className={styles.intradayToggleLabel}>
            Intraday
            <InfoTooltip text="When enabled, only stock legs and intraday signals (MA Crossover, On Schedule, On Exit) are allowed. Backtest is disabled and this strategy can only be traded." />
          </span>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={intradayEnabled}
              onChange={(e) => setIntradayEnabled(e.target.checked)}
              className={styles.toggleInput}
            />
            <span className={styles.toggleSwitch} />
          </label>
        </div>
        {capabilities.showDateRange && (
          <div className={styles.dateSection}>
            <label className={styles.label}>Date Range <InfoTooltip text="The backtest simulation period. The strategy will be evaluated from the start date to the end date using historical market data." /></label>
            <div className={styles.dateInputs}>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                className={styles.dateInput}
                placeholder="Start date"
              />
              <span className={styles.dateSeparator}>to</span>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                className={styles.dateInput}
                placeholder="End date"
              />
            </div>
          </div>
        )}
      </div>

      {/* Symbol selection */}
      {capabilities.symbolMode === "multi" ? (
        <SymbolBank
          symbolBank={effectiveSymbolBank}
          onAddSymbol={addSymbol}
          onAddSymbols={addSymbols}
          onClearSymbols={clearSymbols}
          onRemoveSymbol={removeSymbol}
        />
      ) : (
        <div className={styles.singleSymbolSection}>
          <label className={styles.label}>Strategy Symbol <InfoTooltip text="The stock or ETF symbol this strategy trades. All entry and exit signals will be evaluated against this symbol." /></label>
          <div className={styles.singleSymbolInputRow}>
            <TickerDropdown
              value={selectedSingleSymbol}
              onChange={setSingleSymbol}
              onCommit={setSingleSymbol}
              placeholder="Select symbol (single symbol only)..."
              className={styles.singleSymbolDropdown}
            />
          </div>
          <div className={styles.nameHelperText}>
            Single symbol mode is used for Strategy Order Ticket on {capabilities.contextLabel}.
          </div>
          {symbolModeNotice && (
            <div className={styles.symbolModeNotice}>{symbolModeNotice}</div>
          )}
        </div>
      )}

      {/* Strategy Signals Section */}
      <div className={styles.strategySignalsSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Strategy Signals <InfoTooltip text="Define when to enter and exit positions. Entry signals trigger opening a position, exit signals trigger closing it. Signals within a group are AND-ed together; groups are OR-ed." /></h3>
        </div>

        <div className={styles.strategySignalsContent}>
          <div className={styles.signalsRow}>
            {/* Entry Signals */}
            <div className={styles.signalColumn}>
              <OrGroupEditor
                groups={strategyEntryRules}
                legId="strategy"
                label="Entry"
                category="entry"
                onAddSignal={addStrategyEntrySignal}
                onRemoveSignal={removeStrategyEntrySignal}
                onUpdateSignal={updateStrategyEntrySignal}
                onAddGroup={addStrategyEntryGroup}
                onRemoveGroup={removeStrategyEntryGroup}
                allowedSignalTypes={strategyEntryAllowedSignalTypes}
                copiedSignal={copiedSignal}
                onCopySignal={handleCopySignal}
              />
            </div>

            {/* Exit Signals */}
            <div className={styles.signalColumn}>
              <OrGroupEditor
                groups={strategyExitRules}
                legId="strategy"
                label="Exit"
                category="exit"
                onAddSignal={addStrategyExitSignal}
                onRemoveSignal={removeStrategyExitSignal}
                onUpdateSignal={updateStrategyExitSignal}
                onAddGroup={addStrategyExitGroup}
                onRemoveGroup={removeStrategyExitGroup}
                allowedSignalTypes={strategyExitAllowedSignalTypes}
                copiedSignal={copiedSignal}
                onCopySignal={handleCopySignal}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Legs Section */}
      <div className={styles.legsSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Strategy Legs <InfoTooltip text="Each leg represents a separate instrument to trade (e.g., a stock, option, or different symbol). Legs can have their own entry/exit signals and trade conditions." /></h3>
          <button onClick={addLeg} className={styles.addButton}>
            + Add Leg
          </button>
        </div>

        <div className={styles.legsContainer}>
          <AnimatePresence initial={false}>
            {legs.map((leg, index) => (
              <ItemWrap key={leg.id}>
                <LegCard
                  leg={leg}
                  symbolBank={effectiveSymbolBank}
                  onRemoveLeg={removeLeg}
                  onAddEntrySignal={addEntrySignal}
                  onRemoveEntrySignal={removeEntrySignal}
                  onUpdateEntrySignal={updateEntrySignal}
                  onAddEntryGroup={addEntryGroup}
                  onRemoveEntryGroup={removeEntryGroup}
                  onUpdateTradeCondition={updateTradeConditionInLeg}
                  onAddExitSignal={addExitSignal}
                  onRemoveExitSignal={removeExitSignal}
                  onUpdateExitSignal={updateExitSignal}
                  onAddExitGroup={addExitGroup}
                  onRemoveExitGroup={removeExitGroup}
                  legIndex={index}
                  intradayEnabled={intradayEnabled}
                  allowedSignalTypes={allowedSignalTypes}
                  entryAllowedSignalTypes={legEntryAllowedSignalTypes}
                  exitAllowedSignalTypes={legExitAllowedSignalTypes}
                  copiedSignal={copiedSignal}
                  onCopySignal={handleCopySignal}
                  copiedLeg={copiedLeg}
                  onCopyLeg={handleCopyLeg}
                  onPasteLeg={handlePasteLeg}
                />
              </ItemWrap>
            ))}
          </AnimatePresence>
          {legs.length === 0 && (
            <div className={styles.emptyState}>
              <p>No legs added yet. Click "+ Add Leg" to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Strategy name + action buttons */}
      <div className={styles.submitSection}>
        <div className={styles.strategyNameFooter}>
          <label className={styles.label}>Strategy Name <InfoTooltip text="A unique name for your strategy. Names are case- and space-insensitive, so 'My Strategy' and 'my strategy' are treated as the same." /></label>
          <input
            value={strategyName}
            onChange={(e) => setStrategyName(e.target.value)}
            className={styles.strategyInput}
            placeholder="Strategy Name"
          />
          <div className={styles.nameHelperText}>
            Names are case- and space-insensitive.
          </div>
          {nameValidationError && (
            <div className={styles.nameValidationError}>{nameValidationError}</div>
          )}
          {!nameValidationError && isCheckingName && (
            <div className={styles.nameValidationChecking}>Checking name availability...</div>
          )}
        </div>
        {tradeValidationErrors.length > 0 && (
          <div className={styles.validationError}>
            <div className={styles.errorSummary}>
              {tradeValidationErrors.length} Trade validation error(s)
            </div>
            <div className={styles.errorList}>
              {tradeValidationErrors.map((error, idx) => (
                <div key={idx} className={styles.errorItem}>
                  <span className={styles.errorPath}>{error.path}:</span>{' '}
                  <span className={styles.errorMessage}>{error.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {canBacktest && !intradayEnabled && backtestValidationErrors.length > 0 && (
          <div className={styles.validationError}>
            <div className={styles.errorSummary}>
              {backtestValidationErrors.length} Backtest validation error(s)
            </div>
            <div className={styles.errorList}>
              {backtestValidationErrors.map((error, idx) => (
                <div key={idx} className={styles.errorItem}>
                  <span className={styles.errorPath}>{error.path}:</span>{' '}
                  <span className={styles.errorMessage}>{error.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {displayValidationWarnings.length > 0 && (
          <div className={styles.validationWarning}>
            <div className={styles.warningSummary}>
              {displayValidationWarnings.length} validation warning(s)
            </div>
            <div className={styles.warningList}>
              {displayValidationWarnings.map((warning, idx) => (
                <div key={idx} className={styles.warningItem}>
                  <span className={styles.warningPath}>{warning.path}:</span>{' '}
                  <span className={styles.warningMessage}>{warning.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Backtest error message */}
        {submitError && (
          <div className={styles.submitErrorInline}>{submitError}</div>
        )}

        {tradeNotice && (
          <div className={styles.tradeNoticeInline}>{tradeNotice}</div>
        )}

        <div className={styles.submitButtonsRow}>
          <button
            type="button"
            onClick={resetPersistedState}
            className={styles.resetPersistenceButton}
            disabled={!strategyStorageKey}
            title="Reset"
          >
            Reset
          </button>
          <button
            onClick={handleTrade}
            className={`${styles.submitButton} ${isTradeActionDisabled ? styles.submitButtonDisabled : ''}`}
            disabled={isTradeActionDisabled}
            title={isTradeActionDisabled ? tradeButtonTitle : 'Trade (schedules strategy)'}
            data-testid="strategy-trade-button"
          >
            {isTradeSubmitting ? 'Trading...' : 'Trade'}
          </button>
          {canBacktest && (
            <button
              onClick={handleSubmit}
              className={`${styles.submitButton} ${isBacktestDisabled ? styles.submitButtonDisabled : ''}`}
              disabled={isBacktestDisabled}
              title={backtestButtonTitle}
            >
              {isCheckingName ? 'Checking...' : 'Backtest'}
            </button>
          )}
        </div>
      </div>
      </div>
      {isTradeConfirmOpen && tradeConfirmConfig && (
        <div
          className={styles.tradeConfirmBackdrop}
          data-testid="strategy-trade-confirm-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeTradeConfirmModal();
            }
          }}
        >
          <div
            className={styles.tradeConfirmModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="strategy-trade-confirm-title"
            data-testid="strategy-trade-confirm-modal"
          >
            <div className={styles.tradeConfirmHeader}>
              <h3 id="strategy-trade-confirm-title" className={styles.tradeConfirmTitle}>
                Confirm Strategy Trade
              </h3>
            </div>
            <div className={styles.tradeConfirmBody}>
              <div className={styles.tradeConfirmConfigWrap}>
                <StrategyConfigView config={tradeConfirmConfig} />
              </div>
              <p className={styles.tradeConfirmNote}>
                All strategy orders will be executed at Best Fill, which is the available mid price.
              </p>
              {tradeConfirmError && (
                <div className={styles.tradeConfirmError}>{tradeConfirmError}</div>
              )}
            </div>
            <div className={styles.tradeConfirmActions}>
              <button
                type="button"
                className={styles.tradeConfirmCancel}
                onClick={closeTradeConfirmModal}
                disabled={isTradeSubmitting}
                data-testid="strategy-trade-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.tradeConfirmSubmit}
                onClick={handleConfirmTrade}
                disabled={isTradeSubmitting}
                data-testid="strategy-trade-confirm-submit"
              >
                {isTradeSubmitting ? 'Trading...' : 'Confirm Trade'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isTradeSuccessOpen && (
        <div
          className={styles.tradeConfirmBackdrop}
          data-testid="strategy-trade-success-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeTradeSuccessModal();
            }
          }}
        >
          <div
            className={styles.tradeConfirmModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="strategy-trade-success-title"
            data-testid="strategy-trade-success-modal"
          >
            <div className={styles.tradeConfirmHeader}>
              <h3 id="strategy-trade-success-title" className={styles.tradeConfirmTitle}>
                Strategy Order Scheduled
              </h3>
            </div>
            <div className={styles.tradeConfirmBody}>
              <p className={styles.tradeSuccessMessage}>
                {STRATEGY_TRADE_SUCCESS_MESSAGE}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
